import type { TimelineLabel } from "./labels";

export type ShotCheckpointViewport = "desktop" | "mobile";

export interface ShotCheckpoint {
  id: string;
  label?: TimelineLabel;
  progress: number;
  viewports: readonly ShotCheckpointViewport[];
  purpose: string;
  expectations?: Readonly<Record<string, boolean | number | string>>;
}

export const SHOT_CHECKPOINTS: readonly ShotCheckpoint[] = [
  {
    id: "deep-space",
    label: "deepSpace",
    progress: 0,
    viewports: ["desktop", "mobile"],
    purpose: "Initial full-route state before product objects enter the cinematic scene.",
    expectations: {
      bridgeVisible: false,
      heroCtaVisible: true,
      finalCtaVisible: false,
    },
  },
  {
    id: "ai-chat",
    label: "aiChat",
    progress: 0.18,
    viewports: ["desktop"],
    purpose: "AI chat beat is reachable through the full-route progress API.",
  },
  {
    id: "act-i-code-truth",
    progress: 0.53,
    viewports: ["desktop", "mobile"],
    purpose: "The visible AI answer is clean Blender Python while the Run transport keeps a real blender_python command.",
    expectations: {
      actICommandFormat: "blender_python",
      visibleCodeContains: "Blender Bridge Cube",
      visibleCodeHidesTransportWrapper: true,
    },
  },
  {
    id: "run",
    label: "run",
    progress: 0.56,
    viewports: ["desktop", "mobile"],
    purpose: "The real Run-button beat is reachable after clean Python appears in the AI answer.",
  },
  {
    id: "bridge-reveal",
    progress: 0.875,
    viewports: ["desktop", "mobile"],
    purpose: "Bridge reveal gate exposes the loaded bridge in the full-route timeline.",
    expectations: {
      bridgeVisible: true,
    },
  },
  {
    id: "blender-landing",
    label: "blenderLanding",
    progress: 0.965,
    viewports: ["desktop", "mobile"],
    purpose: "The cube lands on the Blender viewport as a top orthographic plane before the internal Blender camera moves.",
    expectations: {
      bridgeVisible: true,
      blenderViewBlendMax: 0.12,
      metaverseBlendMax: 0.05,
      finalCtaVisible: false,
      canvasClippedToViewport: false,
      globalNavVisible: false,
    },
  },
  {
    id: "blender-perspective",
    label: "blender",
    progress: 0.9955,
    viewports: ["desktop"],
    purpose: "The internal Blender viewport camera turns the plane into the recognizable default 3D perspective.",
    expectations: {
      bridgeVisible: false,
      blenderViewBlendMin: 0.55,
      metaverseBlendMax: 0.05,
      finalCtaVisible: false,
      canvasClippedToViewport: true,
      globalNavVisible: false,
    },
  },
  {
    id: "metaverse",
    label: "metaverse",
    progress: 0.997,
    viewports: ["desktop"],
    purpose: "The Blender default grid transforms into the canonical Deep Space metaverse environment.",
    expectations: {
      bridgeVisible: false,
      blenderViewBlendMin: 0.95,
      metaverseBlendMin: 0.85,
      finalCtaVisible: false,
      canvasClippedToViewport: true,
      globalNavVisible: false,
    },
  },
  {
    id: "final-cta",
    label: "finalCta",
    progress: 1,
    viewports: ["desktop", "mobile"],
    purpose: "The Blender-to-metaverse act stays cinematic and does not introduce the marketing CTA inside Blender.",
    expectations: {
      bridgeVisible: false,
      metaverseBlendMin: 0.95,
      finalCtaVisible: false,
      canvasClippedToViewport: true,
      globalNavVisible: false,
    },
  },
] as const;
